import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  Outlet,
} from '@tanstack/react-router';
import type { RouterContext } from './lib/router-context';
import { LoginPage } from './pages/login';
import { AppShell } from './components/app-shell';
import { TenantsListPage } from './pages/tenants-list';
import { TenantNewPage } from './pages/tenant-new';
import { TenantDetailPage } from './pages/tenant-detail';
import { UsersListPage } from './pages/users-list';
import { UserDetailPage } from './pages/user-detail';
import { StaffListPage } from './pages/staff-list';
import { StaffNewPage } from './pages/staff-new';
import { StaffDetailPage } from './pages/staff-detail';
import { RolesListPage } from './pages/roles-list';
import { RoleNewPage } from './pages/role-new';
import { RoleDetailPage } from './pages/role-detail';
import { BusinessTypesListPage } from './pages/business-types-list';
import { BusinessTypeNewPage } from './pages/business-type-new';
import { BusinessTypeDetailPage } from './pages/business-type-detail';
import { AssistantInboxPage } from './pages/assistant-inbox';
import { AssistantEscalationDetailPage } from './pages/assistant-escalation-detail';
import { KnowledgeEditorPage } from './pages/knowledge-editor';
import { EscalationChatPage } from './pages/escalation-chat';
import { AssistantAnalyticsPage } from './pages/assistant-analytics';
import { CostPlusPage } from './pages/cost-plus';
import { OnboardingQueuePage } from './pages/onboarding-queue';
import { OnboardingFunnelPage } from './pages/onboarding-funnel';
import { OnboardingEventsPage } from './pages/onboarding-events';
import { AccountingModulesPage } from './pages/accounting-modules';
import { FeedbackInboxPage } from './pages/feedback-inbox';
import { FeedbackDetailPage } from './pages/feedback-detail';
import { ModuleMatrixPage } from './pages/module-matrix';
import { WorkerPage } from './pages/worker/worker-page';
import { PERMS } from '../rbac/permissions';

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Already signed in? Don't sit on the login page — send them to the app.
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'authenticated') throw redirect({ to: '/tenants' });
  },
  component: LoginPage,
});
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  beforeLoad: ({ context, location }) => {
    if (context.auth.status === 'loading') return;
    if (context.auth.status !== 'authenticated')
      throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  component: AppShell,
});
const tenantsListRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/tenants',
  component: TenantsListPage,
  beforeLoad: ({ context }) => {
    // Wait for auth to resolve before checking perms — otherwise the bootstrap
    // 'loading' window (hasPerm() === false) bounces /tenants -> /login -> /tenants.
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.TENANTS_READ)) throw redirect({ to: '/login' });
  },
});
const tenantNewRoute = createRoute({
  getParentRoute: () => authRoute,
  // MUST be registered before '$tenantId' below: 'new' would otherwise bind
  // as a tenantId (same trap as /staff/new, /admin-roles/new, /business-types/new).
  path: '/tenants/new',
  component: TenantNewPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.TENANTS_CREATE)) throw redirect({ to: '/tenants' });
  },
});
// Nine tabs, V1's order (spec §2.1 / §7.1) -- an unknown/omitted `tab` value
// resolves client-side to Overview (tenant-detail.tsx's DEFAULT_TAB), so this
// validator only needs to recognize the nine legal keys and drop anything
// else, never itself pick a default.
const TENANT_DETAIL_TAB_IDS = [
  'overview',
  'onboarding',
  'organization',
  'modules',
  'roles',
  'users',
  'notes',
  'audit',
  'accounting',
] as const;
type TenantDetailTabId = (typeof TENANT_DETAIL_TAB_IDS)[number];
const tenantDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/tenants/$tenantId',
  validateSearch: (search): { tab?: TenantDetailTabId } => {
    const tab = search['tab'];
    if (typeof tab === 'string' && (TENANT_DETAIL_TAB_IDS as readonly string[]).includes(tab)) {
      return { tab: tab as TenantDetailTabId };
    }
    return {};
  },
  component: TenantDetailPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.TENANTS_READ)) throw redirect({ to: '/login' });
  },
});
const usersListRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/users',
  component: UsersListPage,
  beforeLoad: ({ context }) => {
    // The loading guard is load-bearing: without it a hard refresh redirects an
    // authorized operator to /login before their permissions have resolved.
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.USERS_READ)) throw redirect({ to: '/login' });
  },
});
const userDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/users/$userId',
  component: UserDetailPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.USERS_READ)) throw redirect({ to: '/login' });
  },
});
const staffListRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/staff',
  component: StaffListPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.STAFF_READ)) throw redirect({ to: '/login' });
  },
});
const staffNewRoute = createRoute({
  getParentRoute: () => authRoute,
  // MUST be registered before '$uid' below: 'new' would otherwise bind as a uid.
  path: '/staff/new',
  component: StaffNewPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.STAFF_MANAGE)) throw redirect({ to: '/login' });
  },
});
const staffDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/staff/$uid',
  component: StaffDetailPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.STAFF_READ)) throw redirect({ to: '/login' });
  },
});
const adminRolesListRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin-roles',
  component: RolesListPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ROLES_MANAGE)) throw redirect({ to: '/login' });
  },
});
const adminRoleNewRoute = createRoute({
  getParentRoute: () => authRoute,
  // MUST be registered before '$roleId' below: 'new' would otherwise bind as a
  // roleId.
  path: '/admin-roles/new',
  component: RoleNewPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ROLES_MANAGE)) throw redirect({ to: '/login' });
  },
});
const adminRoleDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin-roles/$roleId',
  component: RoleDetailPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ROLES_MANAGE)) throw redirect({ to: '/login' });
  },
});
const businessTypesListRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/business-types',
  component: BusinessTypesListPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.BUSINESS_TYPES_READ)) throw redirect({ to: '/login' });
  },
});
const businessTypeNewRoute = createRoute({
  getParentRoute: () => authRoute,
  // MUST be registered before '$businessType' below: 'new' would otherwise
  // bind as a slug.
  path: '/business-types/new',
  component: BusinessTypeNewPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.BUSINESS_TYPES_WRITE)) throw redirect({ to: '/login' });
  },
});
const businessTypeDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/business-types/$businessType',
  component: BusinessTypeDetailPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.BUSINESS_TYPES_READ)) throw redirect({ to: '/login' });
  },
});
const assistantInboxRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/assistant',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ASSISTANT_REVIEW)) throw redirect({ to: '/tenants' });
  },
  component: AssistantInboxPage,
});
const assistantDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/assistant/$escalationId',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ASSISTANT_REVIEW)) throw redirect({ to: '/tenants' });
  },
  component: function AssistantDetailWrapper() {
    const { escalationId } = assistantDetailRoute.useParams();
    return <AssistantEscalationDetailPage escalationId={escalationId} />;
  },
});
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tenants' });
  },
});

const knowledgeEditorRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/assistant/knowledge',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ASSISTANT_CURATE)) throw redirect({ to: '/login' });
  },
  component: KnowledgeEditorPage,
});

const escalationChatRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/assistant/$escalationId/chat',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ASSISTANT_RESOLVE)) throw redirect({ to: '/tenants' });
  },
  component: function EscalationChatWrapper() {
    const { escalationId } = escalationChatRoute.useParams();
    return <EscalationChatPage escalationId={escalationId} />;
  },
});

const onboardingEventsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/tenants/$tenantId/onboarding/events',
  component: function OnboardingEventsRouteComponent() {
    const { tenantId } = onboardingEventsRoute.useParams();
    return <OnboardingEventsPage tenantId={tenantId} />;
  },
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ONBOARDING_AUDIT_VIEW)) throw redirect({ to: '/login' });
  },
});

const accountingModulesRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/tenants/$tenantId/accounting-modules',
  component: function AccountingModulesRouteComponent() {
    const { tenantId } = accountingModulesRoute.useParams();
    return <AccountingModulesPage tenantId={tenantId} />;
  },
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ACCOUNTING_MODULES_READ)) throw redirect({ to: '/login' });
  },
});

const onboardingQueueRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/onboarding',
  component: OnboardingQueuePage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ONBOARDING_VIEW)) throw redirect({ to: '/tenants' });
  },
});

const onboardingFunnelRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/onboarding/funnel',
  component: OnboardingFunnelPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ONBOARDING_VIEW)) throw redirect({ to: '/tenants' });
  },
});

const assistantAnalyticsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/assistant/analytics',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ASSISTANT_REVIEW)) throw redirect({ to: '/tenants' });
  },
  component: AssistantAnalyticsPage,
});

const costPlusRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/cost-plus',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.COST_PLUS_READ)) throw redirect({ to: '/tenants' });
  },
  component: CostPlusPage,
});

const feedbackInboxRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/feedback',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.FEEDBACK_REVIEW)) throw redirect({ to: '/tenants' });
  },
  component: FeedbackInboxPage,
});
const feedbackDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/feedback/$id',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.FEEDBACK_REVIEW)) throw redirect({ to: '/tenants' });
  },
  component: function FeedbackDetailWrapper() {
    const { id } = feedbackDetailRoute.useParams();
    return <FeedbackDetailPage id={id} />;
  },
});

const moduleMatrixRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/modules/matrix',
  component: ModuleMatrixPage,
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.ENTITLEMENTS_READ)) throw redirect({ to: '/tenants' });
  },
});

// E-3 L3 Worker page (P2-H01): one route, `?section=` picks the section. The
// section list is discovered per file (pages/worker/sections.ts), so a new
// section never edits this route.
const workerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/worker',
  validateSearch: (search): { section?: string } => {
    const section = search['section'];
    return typeof section === 'string' && /^[a-z][a-z0-9-]{0,63}$/.test(section) ? { section } : {};
  },
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'loading') return;
    if (!context.auth.hasPerm(PERMS.WORKER_READ)) throw redirect({ to: '/tenants' });
  },
  component: function WorkerRouteComponent() {
    const { section } = workerRoute.useSearch();
    const navigate = useNavigate();
    return (
      <WorkerPage
        section={section}
        onSectionChange={(key) => void navigate({ to: '/worker', search: { section: key } })}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  authRoute.addChildren([
    tenantsListRoute,
    tenantNewRoute,
    tenantDetailRoute,
    usersListRoute,
    userDetailRoute,
    staffListRoute,
    staffNewRoute,
    staffDetailRoute,
    adminRolesListRoute,
    adminRoleNewRoute,
    adminRoleDetailRoute,
    businessTypesListRoute,
    businessTypeNewRoute,
    businessTypeDetailRoute,
    assistantInboxRoute,
    assistantDetailRoute,
    escalationChatRoute,
    knowledgeEditorRoute,
    assistantAnalyticsRoute,
    costPlusRoute,
    onboardingQueueRoute,
    onboardingFunnelRoute,
    onboardingEventsRoute,
    accountingModulesRoute,
    feedbackInboxRoute,
    feedbackDetailRoute,
    moduleMatrixRoute,
    workerRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: undefined as unknown as RouterContext,
  defaultPreload: 'intent',
});
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
